import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { classifyWalletMfaError, WalletMfaErrorKind } from './wallet-mfa-errors';
import {
  type WalletMfaError,
  WalletMfaVerificationPhase,
  WalletMfaVerificationPurpose,
} from './wallet-mfa-types';
import {
  createClosedVerificationState,
  createVerificationState,
  RESTART_AFTER_MAX_ATTEMPTS,
  RESTART_AFTER_TIMEOUT,
  VERIFICATION_IDLE_TIMEOUT_MS,
  type WalletMfaVerificationState,
} from './wallet-mfa-verification-state';

export type VerificationRun = {
  id: number;
  active: boolean;
  purpose: WalletMfaVerificationPurpose;
  autoSubmitted: boolean;
  submitting: boolean;
};

export type VerificationSession = {
  state: WalletMfaVerificationState;
  setState: Dispatch<SetStateAction<WalletMfaVerificationState>>;
  runRef: RefObject<VerificationRun>;
  startIdleTimer: (_onTimeout: () => void) => void;
  finish: () => void;
  failClosed: (_message: string) => void;
};

const idleRun = (id: number): VerificationRun => ({
  id,
  active: false,
  purpose: WalletMfaVerificationPurpose.Transaction,
  autoSubmitted: false,
  submitting: false,
});

const restartMessageFor = (error: WalletMfaError): string => {
  if (error.kind === WalletMfaErrorKind.Timeout) {
    return RESTART_AFTER_TIMEOUT;
  }

  if (error.kind === WalletMfaErrorKind.MaxAttempts) {
    return RESTART_AFTER_MAX_ATTEMPTS;
  }

  return error.message;
};

const useVerificationIdleTimer = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = null;
  }, []);

  const start = useCallback(
    (onTimeout: () => void) => {
      clear();
      timerRef.current = setTimeout(onTimeout, VERIFICATION_IDLE_TIMEOUT_MS);
    },
    [clear],
  );

  useEffect(() => clear, [clear]);

  return { start, clear };
};

export const useVerificationSession = (cancel: () => void): VerificationSession => {
  const [state, setState] = useState<WalletMfaVerificationState>(createClosedVerificationState);
  const runRef = useRef<VerificationRun>(idleRun(0));
  const { start: startIdleTimer, clear: clearIdleTimer } = useVerificationIdleTimer();

  const finish = useCallback(() => {
    clearIdleTimer();
    runRef.current = idleRun(runRef.current.id + 1);
    setState(createClosedVerificationState());
  }, [clearIdleTimer]);

  const failClosed = useCallback(
    (message: string) => {
      clearIdleTimer();
      runRef.current.id += 1;
      cancel();
      setState(
        createVerificationState(WalletMfaVerificationPhase.Error, runRef.current.purpose, {
          error: message,
        }),
      );
    },
    [cancel, clearIdleTimer],
  );

  return { state, setState, runRef, startIdleTimer, finish, failClosed };
};

export const useVerificationErrorHandler = ({
  runRef,
  setState,
  failClosed,
}: VerificationSession) =>
  useCallback(
    (error: unknown) => {
      const classified = classifyWalletMfaError(error);

      if (classified.kind === WalletMfaErrorKind.InvalidCode) {
        setState(
          createVerificationState(WalletMfaVerificationPhase.Totp, runRef.current.purpose, {
            error: classified.message,
          }),
        );
        return;
      }

      failClosed(restartMessageFor(classified));
    },
    [failClosed, runRef, setState],
  );
